import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { apiParams } from '../lib/filters.js';
import { CONFIDENCE_TIERS, STATUS_STATES } from '../lib/palette.js';
import {
  COLUMN_SORTS,
  KIND_VOCABULARY,
  MATRIX_PARAM_KEYS,
  NONPROJECT_KEY,
  ROW_SORTS,
  cellLabel,
  columnExportRows,
  kindOf,
  matrixExportRows,
  matrixModel,
  matrixSummary,
  resolveWeek,
  rowExportRows,
  weekOptions,
  matrixCellColumns,
  matrixColumnColumns,
  matrixPeopleColumns,
} from '../lib/matrix.js';
import { withFormats } from '../lib/table.js';
import {
  hours as formatHours,
  percent,
  weekLabel,
  longDate,
  count,
  humanize,
} from '../lib/format.js';

const COLUMN_SORT_LABELS = { hours: 'Hours', name: 'Name', type: 'Type' };
const ROW_SORT_LABELS = { name: 'Name', utilization: 'Load', hours: 'Hours' };
const COLUMN_SORTS_UI = COLUMN_SORTS.map((key) => ({ key, label: COLUMN_SORT_LABELS[key] }));
const ROW_SORTS_UI = ROW_SORTS.map((key) => ({ key, label: ROW_SORT_LABELS[key] }));
const MARKER = Object.fromEntries(CONFIDENCE_TIERS.map((t) => [t.key, t.marker]));
const TIER_LABEL = Object.fromEntries(CONFIDENCE_TIERS.map((t) => [t.key, t.label]));

/** What the owner a column head names is to the record (`owner_source` from the API). */
const OWNER_LABEL = {
  project_manager: 'Project manager',
  account_manager: 'Account manager',
  deal_owner: 'Deal owner',
};

/** `1 person`, `3 people`. */
const plural = (n, one, many = `${one}s`) => `${count(n)} ${n === 1 ? one : many}`;
/** Hours in a cell: no unit, one decimal at most, so `18`, `6.5`, `0.4`. */
const cellHours = (h) => formatHours(h, { unit: false });

/**
 * The assignment matrix (PRD 10.6; V-8; Phase 28): for one week, who is on what. People down the
 * side, the projects, retainers and deals with hours that week across the top, the hours in the
 * cells, and each person's total against their capacity at the right. It is the view a traffic
 * manager operates in, read-only until Phase 3 makes it editable.
 *
 * Everything drawn here comes from `GET /forecast/matrix` for the selected week. The row total
 * is the API's own figure — the same `weekCell` the heatmap builds a person's week from, so a
 * person at 117% here is at 117% there — and the cells always sum to it, because the non-project
 * baseline is a column too (last, and quiet) rather than an unexplained remainder in the total.
 *
 * Encoding (PRD 7.3, Appendix A):
 *   fill     the cell's confidence tier — solid, hatched, dotted — with the tier's marker beside
 *            the number, so the pattern is never the only cue
 *   hue      the column's project type, as a left rule on the cell and a bar over the column head
 *   border   the record status: a dashed rule where the column is a deal
 *   status   the row total alone wears the reserved threshold colours, and only caution and
 *            overloaded are painted; a healthy or under-utilized total is quiet, with its icon
 *
 * The controls hide columns in the browser and never change a row's total: a person's week is
 * their week whether or not the deals are ticked. Section 17 of UI Client.md says why this is the
 * opposite of the timeline's rule. What the controls hide is stated beside each total as
 * `hidden`, so the eye is never left to add up a row that does not.
 */
class AssignmentMatrix extends StoreMixin(LitElement) {
  static properties = {
    // The page's selected week, shared with the contention map below (Phase 44). The card still
    // keeps its own, so it works standing on its own; the page's wins when there is one.
    week: { type: String },
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _week: { state: true },
    _kinds: { state: true },
    _columnSort: { state: true },
    _rowSort: { state: true },
    _q: { state: true },
    _hover: { state: true },
    _selectedRow: { state: true },
    _selectedColumn: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this.week = null;
    this._data = null;
    this._loading = false;
    this._error = null;
    this._week = null;
    this._kinds = KIND_VOCABULARY.map((k) => k.key);
    this._columnSort = 'hours';
    this._rowSort = 'name';
    this._q = '';
    this._hover = null;
    this._selectedRow = null;
    this._selectedColumn = null;
    this._tableOpen = false;
    this._requestKey = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
        min-width: 0;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
        min-width: 0;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 12px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
        margin-top: 4px;
      }
      .kpis {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 22px;
        margin-left: auto;
      }
      .kpi .n {
        font-size: 22px;
        font-weight: 700;
        line-height: 1.1;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
      }
      .kpi .l {
        font-size: 11.5px;
        color: var(--ink-muted);
      }

      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 18px;
        margin: 4px 0 12px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .controls .group {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .controls label.kind {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        color: var(--ink);
        cursor: pointer;
      }
      .segmented {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented button {
        font: inherit;
        font-size: 12px;
        font-weight: 600;
        color: var(--ink-muted);
        background: none;
        border: 0;
        border-radius: var(--radius-pill);
        padding: 3px 10px;
        cursor: pointer;
      }
      .segmented button[aria-pressed='true'] {
        /* --ink-strong is near-white in the dark theme; the surface colour is the label that
         clears it in both (see pages.js). */
        color: var(--surface);
        background: var(--ink-strong);
      }
      .week {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
      .week select,
      .search input {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: 6px;
        padding: 4px 8px;
      }
      .week button {
        font: inherit;
        font-size: 12px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: 6px;
        padding: 3px 8px;
        cursor: pointer;
      }
      .week button[disabled] {
        opacity: 0.5;
        cursor: not-allowed;
      }
      .search input {
        min-width: 220px;
      }

      .scroll {
        overflow: auto;
        max-height: 72vh;
        border: 1px solid var(--border-soft);
        border-radius: 8px;
      }
      table.matrix {
        border-collapse: separate;
        border-spacing: 0;
        font-size: 12.5px;
        min-width: 100%;
      }
      table.matrix th,
      table.matrix td {
        padding: 3px 4px;
        border-bottom: 1px solid var(--border-soft);
        background: var(--surface);
        vertical-align: middle;
        white-space: nowrap;
      }
      table.matrix thead th {
        position: sticky;
        top: 0;
        z-index: 3;
        vertical-align: bottom;
        text-align: left;
        font-weight: 600;
        color: var(--ink-muted);
        border-bottom: 2px solid var(--border);
        background: var(--surface);
      }
      table.matrix th.corner,
      table.matrix tbody th[scope='row'],
      table.matrix tfoot th[scope='row'] {
        position: sticky;
        left: 0;
        z-index: 2;
        min-width: 150px;
        max-width: 220px;
        text-align: left;
        box-shadow: inset -1px 0 0 var(--border-soft);
      }
      table.matrix th.corner {
        z-index: 4;
      }
      table.matrix tbody th[scope='row'] {
        font-weight: 600;
        color: var(--ink);
      }
      table.matrix tbody th[scope='row'] button,
      table.matrix thead th.col button {
        font: inherit;
        color: inherit;
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
        display: block;
        width: 100%;
      }
      table.matrix tbody th[scope='row'] button:hover,
      table.matrix thead th.col button:hover .name {
        color: var(--link);
        text-decoration: underline;
      }
      table.matrix th[scope='row'] .open,
      table.matrix thead th.col .open {
        display: block;
        font: inherit;
        font-size: 11px;
        font-weight: 600;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
      }
      table.matrix th[scope='row'] .open:hover,
      table.matrix thead th.col .open:hover {
        text-decoration: underline;
      }
      /* The column's own link sits under the head, and is not the pin: width follows its words. */
      table.matrix thead th.col .open {
        width: auto;
        margin-top: 2px;
      }
      table.matrix th[scope='row'] .small {
        display: block;
        font-size: 11px;
        color: var(--ink-muted);
        font-weight: 400;
      }
      table.matrix tr[aria-selected='true'] th[scope='row'] {
        color: var(--ink-strong);
        font-weight: 700;
        box-shadow:
          inset 3px 0 0 var(--accent),
          inset -1px 0 0 var(--border-soft);
      }
      table.matrix tr[aria-selected='true'] td {
        background: var(--surface-2);
      }
      table.matrix td[data-selected='true'],
      table.matrix th.col[data-selected='true'] {
        background: var(--surface-2);
      }
      table.matrix th.col[data-selected='true'] .name {
        color: var(--ink-strong);
        font-weight: 700;
      }

      /* A column head: the type hue as a bar, the name on at most two lines, the kind and the
       tier beneath. */
      table.matrix th.col {
        width: 88px;
        min-width: 88px;
        max-width: 88px;
        white-space: normal;
        padding-top: 6px;
      }
      th.col .bar {
        display: block;
        height: 3px;
        border-radius: 2px;
        background: var(--hue, transparent);
        margin-bottom: 4px;
      }
      th.col[data-kind='nonproject'] .bar {
        background: var(--border);
      }
      th.col .name {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        font-size: 11.5px;
        line-height: 1.25;
        color: var(--ink);
        font-weight: 600;
        min-height: 2.5em;
      }
      th.col .meta {
        display: flex;
        align-items: center;
        gap: 4px;
        margin-top: 3px;
        font-weight: 400;
      }
      .chip {
        display: inline-block;
        font-size: 9.5px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        line-height: 1;
        padding: 2px 5px;
        border-radius: var(--radius-pill);
        border: 1px solid var(--border);
        color: var(--ink-muted);
      }
      .chip[data-status='opportunity'] {
        border-style: dashed;
      }
      .chip[data-status='nonproject'] {
        border-color: transparent;
        background: var(--surface-2);
      }
      .chip.warn {
        border-color: var(--status-caution);
        color: var(--ink);
      }
      /* A tier swatch: the fill pattern the cells of this column mostly wear, and its marker. */
      .fill {
        display: inline-block;
        width: 14px;
        height: 10px;
        border-radius: 2px;
        background: var(--swatch, var(--ink-muted));
        vertical-align: middle;
      }
      .fill[data-fill='hatch'] {
        background-image: repeating-linear-gradient(
          45deg,
          transparent 0 2px,
          var(--hatch-ink) 2px 3.5px
        );
      }
      .fill[data-fill='dots'] {
        background-image: radial-gradient(var(--dot-ink) 1px, transparent 1.2px);
        background-size: 4px 4px;
      }
      .marker {
        font-size: 10px;
        font-weight: 700;
        color: var(--ink-muted);
      }

      /* One cell: the type hue as a left rule and a tint, the tier as the pattern over it and the
       marker beside the number. A deal's rule is dashed. */
      table.matrix td {
        text-align: right;
        min-width: 88px;
      }
      table.matrix td.empty {
        background: var(--surface);
      }
      .cell {
        --tint: color-mix(in srgb, var(--hue, var(--ink-muted)) 16%, var(--surface));
        --pattern-ink: color-mix(in srgb, var(--hue, var(--ink-muted)) 55%, transparent);
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 3px;
        width: 100%;
        min-height: 28px;
        padding: 0 6px;
        border-radius: 4px;
        border-left: 3px solid var(--hue, var(--ink-muted));
        background-color: var(--tint);
        color: var(--ink-strong);
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        cursor: crosshair;
        outline-offset: -2px;
        box-sizing: border-box;
      }
      .cell[data-fill='hatch'] {
        background-image: repeating-linear-gradient(
          45deg,
          transparent 0 3px,
          var(--pattern-ink) 3px 4.5px
        );
      }
      .cell[data-fill='dots'] {
        background-image: radial-gradient(var(--pattern-ink) 1.1px, transparent 1.3px);
        background-size: 6px 6px;
      }
      .cell[data-status='opportunity'] {
        border-left-style: dashed;
      }
      .cell[data-kind='nonproject'] {
        --hue: var(--border);
        color: var(--ink-muted);
        font-weight: 500;
      }
      .cell .marker {
        color: var(--ink-strong);
      }
      .cell.hover {
        box-shadow: inset 0 0 0 2px var(--ink-strong);
      }

      /* The row total: hours over capacity, and the load in the band's reserved colour — painted
       only for caution and overloaded; the rest are quiet, with the band's icon. */
      table.matrix td.total,
      table.matrix td.load {
        position: sticky;
        z-index: 2;
        background: var(--surface);
        font-weight: 600;
        color: var(--ink);
        font-variant-numeric: tabular-nums;
      }
      table.matrix td.total {
        right: 92px;
        min-width: 96px;
        border-left: 2px solid var(--border);
        box-shadow: inset 1px 0 0 var(--border-soft);
      }
      table.matrix td.load {
        right: 0;
        min-width: 92px;
      }
      table.matrix thead th.total,
      table.matrix thead th.load {
        position: sticky;
        z-index: 4;
        background: var(--surface);
        text-align: right;
      }
      table.matrix thead th.total {
        right: 92px;
        border-left: 2px solid var(--border);
      }
      table.matrix thead th.load {
        right: 0;
      }
      .total .cap {
        color: var(--ink-muted);
        font-weight: 400;
      }
      .total .hidden {
        display: block;
        font-size: 10.5px;
        font-weight: 400;
        color: var(--ink-muted);
      }
      .load .state {
        display: inline-flex;
        align-items: center;
        justify-content: flex-end;
        gap: 5px;
        width: 100%;
        min-height: 28px;
        padding: 0 8px;
        border-radius: 4px;
        box-sizing: border-box;
        cursor: crosshair;
      }
      .load .state .i {
        font-size: 9px;
        line-height: 1;
      }
      .load .state[data-band='underload'],
      .load .state[data-band='healthy'] {
        color: var(--ink-muted);
      }
      .load .state[data-band='caution'] {
        background: var(--status-caution);
        color: var(--status-caution-ink);
      }
      .load .state[data-band='overloaded'] {
        background: var(--status-overloaded);
        color: var(--status-overloaded-ink);
      }
      .load .state[data-band='none'] {
        background: repeating-linear-gradient(
          45deg,
          var(--surface-2),
          var(--surface-2) 3px,
          var(--border-soft) 3px,
          var(--border-soft) 6px
        );
        color: var(--ink-muted);
        font-weight: 500;
      }

      table.matrix tfoot th,
      table.matrix tfoot td {
        border-top: 2px solid var(--border);
        border-bottom: 0;
        background: var(--surface-2);
      }
      table.matrix tfoot td.total,
      table.matrix tfoot td.load,
      table.matrix tfoot th[scope='row'] {
        background: var(--surface-2);
      }
      table.matrix tr.unassigned th,
      table.matrix tr.unassigned td {
        color: var(--ink-muted);
        font-style: italic;
        border-top-width: 1px;
      }
      table.matrix tr.unassigned .cell {
        font-style: normal;
      }
      table.matrix tr.sum th,
      table.matrix tr.sum td {
        font-weight: 700;
      }
      table.matrix tr.sum td .n {
        display: inline-block;
        padding: 0 6px;
      }

      .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 18px;
        margin: 12px 0 0;
        padding: 0;
        list-style: none;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .legend li {
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
      .legend .sw {
        display: inline-block;
        width: 26px;
        height: 12px;
        border-radius: 3px;
      }
      .legend .sw.cell {
        min-height: 0;
        padding: 0;
        cursor: default;
      }
      .legend .sw.state {
        min-height: 0;
        width: auto;
        padding: 0 6px;
        font-size: 11px;
        cursor: default;
      }
      .legend .type {
        width: 12px;
        height: 12px;
        border-radius: 3px;
        background: var(--hue);
      }

      .note {
        margin: 10px 0 0;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .state-msg {
        padding: 28px 0;
        color: var(--ink-muted);
        text-align: center;
      }
      .state-msg.error {
        color: var(--status-overloaded);
      }
      details.table {
        margin-top: 14px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      details.table data-table {
        display: block;
        margin-top: 10px;
      }
    `,
  ];

  // -------------------------------------------------------------------------------------------
  // Data

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  /**
   * The page can set the week from another card; the store does not change when it does. On a
   * microtask, because the fetch sets state and Lit will not have this render finish inside it.
   */
  updated(changed) {
    if (changed.has('week')) queueMicrotask(() => this._maybeFetch());
  }

  /** The weeks the shared window offers, clipped to the run's horizon. */
  get _weeks() {
    const { filters, run } = this.appState;
    return weekOptions(filters, run);
  }

  /** The week drawn: the one chosen if the window still holds it, else the window's first. */
  get _resolvedWeek() {
    return resolveWeek(this._weeks, this.week ?? this._week);
  }

  /**
   * Only the shared filters this view reads, plus the week. The kind toggles, the sorts and the
   * search stay in the browser: they hide columns, and a row's total must not follow them.
   */
  _params() {
    const { run } = this.appState;
    if (!run) return null;
    const week = this._resolvedWeek;
    if (!week) return null;
    const params = apiParams(this.appState.filters, MATRIX_PARAM_KEYS);
    params.week = week;
    return params;
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/forecast/matrix', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._data = data;
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  get _model() {
    if (!this._data) return null;
    const { filters, palette } = this.appState;
    return matrixModel(this._data, {
      kinds: this._kinds,
      types: filters.types,
      tiers: filters.tiers,
      q: this._q,
      person: this._selectedRow,
      columnSort: this._columnSort,
      rowSort: this._rowSort,
      palette: palette ?? [],
    });
  }

  // -------------------------------------------------------------------------------------------
  // Interaction

  _setWeek(weekStart) {
    this._week = weekStart;
    // Announced as well as kept: the contention map draws the same week when its span is a week
    // (Phase 44), and the page is what holds the two together.
    this.dispatchEvent(
      new CustomEvent('week-change', {
        detail: { week_start: weekStart },
        bubbles: true,
        composed: true,
      }),
    );
    this._maybeFetch();
  }

  _stepWeek(delta) {
    const weeks = this._weeks;
    const i = weeks.findIndex((w) => w.week_start === this._resolvedWeek);
    const next = weeks[i + delta];
    if (next) this._setWeek(next.week_start);
  }

  _toggleKind(key) {
    const next = this._kinds.includes(key)
      ? this._kinds.filter((k) => k !== key)
      : [...this._kinds, key];
    // Nothing ticked is not a question anyone is asking; the last one stays on.
    this._kinds = next.length ? next : this._kinds;
  }

  _selectRow(key) {
    this._selectedRow = this._selectedRow === key ? null : key;
  }

  _selectColumn(key) {
    this._selectedColumn = this._selectedColumn === key ? null : key;
  }

  // -------------------------------------------------------------------------------------------
  // Tooltips

  _tooltip() {
    return this.renderRoot.querySelector('chart-tooltip');
  }

  _at(event) {
    const rect = event.currentTarget?.getBoundingClientRect?.();
    return {
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
    };
  }

  _showCell(event, row, column, cell) {
    const tooltip = this._tooltip();
    if (!tooltip) return;
    this._hover = `${row?.key ?? 'unassigned'}|${column.key}`;
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [{ label: 'Hours this week', value: exact(cell.hours) }];
    for (const t of CONFIDENCE_TIERS)
      if (cell.tiers?.[t.key] > 0)
        rows.push({ label: `  ${t.label} confidence`, value: exact(cell.tiers[t.key]) });
    if (cell.services?.length)
      rows.push({ label: 'Role', value: cell.services.map(humanize).join(', ') });
    if (cell.assignments > 0)
      rows.push({ label: 'Workamajig assignments', value: count(cell.assignments) });
    rows.push({
      label: 'Source',
      value: (cell.source_modes ?? []).map(humanize).join(', ') || '—',
    });
    const facts = [
      kindOf(column.kind)?.label ?? column.kind,
      column.client,
      column.number,
      column.canonical_type ? humanize(column.canonical_type) : null,
      cell.slipped ? 're-anchored after slipping' : null,
    ];
    tooltip.show({
      ...this._at(event),
      title: `${row?.label ?? 'Role-only'} · ${column.name}`,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  _showColumn(event, column) {
    const tooltip = this._tooltip();
    if (!tooltip) return;
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [];
    // Who answers for the column, first: the person to ask about everything below it.
    if (column.owner_name)
      rows.push({
        label: OWNER_LABEL[column.owner_source] ?? 'Owner',
        value: column.owner_name,
      });
    rows.push(
      { label: 'Hours this week', value: exact(column.hours) },
      { label: '  named people', value: exact(column.people_hours) },
    );
    if (column.unassigned_hours > 0)
      rows.push({ label: '  role-only', value: exact(column.unassigned_hours) });
    if (column.withheld_hours > 0)
      rows.push({ label: '  people you cannot see', value: exact(column.withheld_hours) });
    for (const t of CONFIDENCE_TIERS)
      if (column.tiers?.[t.key] > 0)
        rows.push({ label: `${t.label} confidence`, value: exact(column.tiers[t.key]) });
    rows.push({ label: 'People', value: count(column.people) });
    const facts = [
      kindOf(column.kind)?.label ?? column.kind,
      column.client,
      column.number,
      column.canonical_type ? humanize(column.canonical_type) : null,
      column.stage_name ? `stage ${column.stage_name}` : null,
      column.probability_used !== null && column.probability_used !== undefined
        ? `${percent(column.probability_used)} probability`
        : null,
      column.target_date ? `due ${longDate(column.target_date)}` : null,
      column.slipped ? 're-anchored after slipping' : null,
      column.burn_risk ? 'burn risk' : null,
      column.past_due ? 'past due' : null,
    ];
    tooltip.show({
      ...this._at(event),
      title: column.name,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  _showTotal(event, row) {
    const tooltip = this._tooltip();
    if (!tooltip) return;
    const t = row.total;
    const state = STATUS_STATES.find((s) => s.key === t.band);
    const exact = (v) => formatHours(v, { exact: true });
    const rows = [
      { label: 'Allocated', value: exact(t.allocated_hours) },
      { label: 'Capacity', value: exact(t.capacity_hours) },
      t.headroom_hours < 0
        ? { label: 'Over capacity', value: exact(-t.headroom_hours) }
        : { label: 'Headroom', value: exact(t.headroom_hours) },
    ];
    for (const [label, v] of [
      ['Committed (explicit)', t.explicit_hours],
      ['Committed (inferred)', t.inferred_hours],
      ['Recurring', t.recurring_hours],
      ['Non-project', t.nonproject_hours],
      ['Pipeline', t.pipeline_hours],
    ])
      if (v > 0) rows.push({ label: `  ${label}`, value: exact(v) });
    if (row.hidden_hours > 0)
      rows.push({ label: 'Hidden by the controls', value: exact(row.hidden_hours) });
    const facts = [
      t.utilization === null ? 'No capacity this week' : `${percent(t.utilization)} allocated`,
      state && t.capacity_hours > 0 ? `${state.icon} ${state.label}` : null,
      `${plural(row.cells_with_hours, 'column')} with hours`,
      'the same figure as the heatmap cell',
    ];
    tooltip.show({
      ...this._at(event),
      title: `${row.label} · week of ${longDate(this._data.week_start)}`,
      rows,
      footer: facts.filter(Boolean).join(' · '),
    });
  }

  _hide() {
    this._hover = null;
    this._tooltip()?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // The grid

  _hue(column) {
    return column.kind === NONPROJECT_KEY ? 'var(--border)' : `var(${column.slot.var})`;
  }

  _columnHead(column) {
    const selected = this._selectedColumn === column.key;
    const kind = kindOf(column.kind);
    const marker = MARKER[column.confidence_tier];
    const flags = [];
    if (column.slipped) flags.push('slipped');
    if (column.burn_risk) flags.push('burn risk');
    if (column.past_due) flags.push('past due');
    return html`<th
      scope="col"
      class="col"
      data-key=${column.key}
      data-kind=${column.kind}
      data-selected=${selected ? 'true' : 'false'}
      style=${`--hue: ${this._hue(column)}`}
    >
      <button
        type="button"
        aria-pressed=${selected ? 'true' : 'false'}
        title=${`${column.name}${column.client ? ` — ${column.client}` : ''}`}
        @click=${() => this._selectColumn(column.key)}
        @pointermove=${(e) => this._showColumn(e, column)}
        @pointerleave=${() => this._hide()}
        @focus=${(e) => this._showColumn(e, column)}
        @blur=${() => this._hide()}
      >
        <span class="bar" aria-hidden="true"></span>
        <span class="name">${column.name}</span>
        <span class="meta">
          <span class="chip ${flags.length ? 'warn' : ''}" data-status=${column.status}
            >${kind?.label ?? column.kind}</span
          >
          ${
            column.confidence_tier
              ? html`<span
                    class="fill"
                    data-fill=${column.fill}
                    style=${`--swatch: ${this._hue(column)}`}
                    role="img"
                    aria-label=${`${TIER_LABEL[column.confidence_tier]} confidence`}
                  ></span
                  >${marker ? html`<span class="marker" aria-hidden="true">${marker}</span>` : nothing}`
              : nothing
          }
        </span>
      </button>
      ${
        // Project Intelligence (PRD 10.5): a project's or retainer's column opens the panel that
        // says where its hours came from, the way a row opens the person's weeks. A deal has no
        // such page here, and the non-project column is nobody's.
        (column.kind === 'project' || column.kind === 'recurring') && column.id
          ? html`<button
              type="button"
              class="open"
              title=${`Open ${column.name} in Project Intelligence`}
              @click=${() => navigate(`/projects/${column.id}`)}
            >
              open →
            </button>`
          : nothing
      }
    </th>`;
  }

  _cell(row, column, cell) {
    if (!cell || !(cell.hours > 0))
      return html`<td
        class="empty"
        data-column=${column.key}
        data-selected=${this._selectedColumn === column.key ? 'true' : 'false'}
      ></td>`;
    const key = `${row?.key ?? 'unassigned'}|${column.key}`;
    const marker = MARKER[cell.confidence_tier];
    return html`<td
      data-column=${column.key}
      data-selected=${this._selectedColumn === column.key ? 'true' : 'false'}
    >
      <span
        class=${`cell ${this._hover === key ? 'hover' : ''}`}
        data-fill=${cell.fill}
        data-tier=${cell.confidence_tier ?? ''}
        data-kind=${column.kind}
        data-status=${column.status}
        style=${`--hue: ${this._hue(column)}`}
        tabindex="0"
        role="img"
        aria-label=${cellLabel(row, column, cell)}
        @pointermove=${(e) => this._showCell(e, row, column, cell)}
        @pointerleave=${() => this._hide()}
        @focus=${(e) => this._showCell(e, row, column, cell)}
        @blur=${() => this._hide()}
      >
        <span class="v">${cellHours(cell.hours)}</span>
        ${marker ? html`<span class="marker" aria-hidden="true">${marker}</span>` : nothing}
      </span>
    </td>`;
  }

  _totalCells(row) {
    const t = row.total;
    const band = t.capacity_hours > 0 ? (t.band ?? 'none') : 'none';
    const state = STATUS_STATES.find((s) => s.key === band);
    const label =
      band === 'none'
        ? `${row.label}: ${formatHours(t.allocated_hours)} allocated, no capacity this week`
        : `${row.label}: ${formatHours(t.allocated_hours)} of ${formatHours(t.capacity_hours)}, ${percent(t.utilization)} allocated, ${state?.label ?? ''}`;
    return html`
      <td class="total" data-band=${band}>
        <span class="n">${cellHours(t.allocated_hours)}</span
        ><span class="cap">/${cellHours(t.capacity_hours)}</span>
        ${
          row.hidden_hours > 0
            ? html`<span class="hidden">${formatHours(row.hidden_hours)} hidden</span>`
            : nothing
        }
      </td>
      <td class="load" data-band=${band}>
        <span
          class="state"
          data-band=${band}
          role="img"
          tabindex="0"
          aria-label=${label}
          @pointermove=${(e) => this._showTotal(e, row)}
          @pointerleave=${() => this._hide()}
          @focus=${(e) => this._showTotal(e, row)}
          @blur=${() => this._hide()}
        >
          ${
            state && band !== 'none'
              ? html`<span class="i" aria-hidden="true">${state.icon}</span>`
              : nothing
          }
          <span class="v">${band === 'none' ? '—' : percent(t.utilization)}</span>
        </span>
      </td>
    `;
  }

  _row(model, row) {
    const selected = this._selectedRow === row.key;
    return html`<tr data-key=${row.key} aria-selected=${selected ? 'true' : 'false'}>
      <th scope="row">
        <button
          type="button"
          aria-pressed=${selected ? 'true' : 'false'}
          title=${selected ? 'Unpin this row and show every column' : 'Pin this row and show only the columns it has hours in'}
          @click=${() => this._selectRow(row.key)}
        >
          ${row.label}
          ${
            row.entity?.department
              ? html`<span class="small">${row.entity.department}</span>`
              : nothing
          }
        </button>
        ${
          row.entity?.id
            ? html`<button
                type="button"
                class="open"
                title=${`Open ${row.label}'s weeks (the individual view)`}
                @click=${() => navigate(`/people/${row.entity.id}`)}
              >
                weeks →
              </button>`
            : nothing
        }
      </th>
      ${model.columns.map((c, i) => this._cell(row, c, row.cells[i]))} ${this._totalCells(row)}
    </tr>`;
  }

  _foot(model) {
    const un = model.unassigned;
    const totals = model.totals;
    const capacity = totals?.capacity_hours ?? 0;
    const band = capacity > 0 ? (totals?.band ?? 'none') : 'none';
    const state = STATUS_STATES.find((s) => s.key === band);
    const who = model.entity ? model.entity.label : 'Everyone';
    return html`<tfoot>
      ${
        un && un.hours > 0
          ? html`<tr class="unassigned" data-key="unassigned">
              <th scope="row">
                Role-only
                <span class="small">demand with nobody named</span>
              </th>
              ${model.columns.map((c, i) => this._cell(null, c, un.cells[i]))}
              <td class="total" data-band="none">
                <span class="n">${cellHours(un.hours)}</span>
                ${
                  un.hidden_hours > 0
                    ? html`<span class="hidden">${formatHours(un.hidden_hours)} hidden</span>`
                    : nothing
                }
              </td>
              <td class="load" data-band="none">
                <span
                  class="state"
                  data-band="none"
                  aria-label="Role-only hours count against no one"
                  >—</span
                >
              </td>
            </tr>`
          : nothing
      }
      <tr class="sum" data-key="sum">
        <th scope="row">
          ${who}
          <span class="small"
            >${plural(totals?.people ?? 0, 'person', 'people')} with
            capacity${
              totals?.withheld_hours > 0 ? `, ${formatHours(totals.withheld_hours)} withheld` : ''
            }</span
          >
        </th>
        ${model.columns.map(
          (c) =>
            html`<td
              data-column=${c.key}
              data-selected=${this._selectedColumn === c.key ? 'true' : 'false'}
            >
              <span
                class="n"
                role="img"
                aria-label=${`${c.name}: ${formatHours(c.hours)} this week`}
                >${cellHours(c.hours)}</span
              >
            </td>`,
        )}
        <td class="total" data-band=${band}>
          <span class="n">${cellHours(totals?.allocated_hours ?? 0)}</span
          ><span class="cap">/${cellHours(capacity)}</span>
        </td>
        <td class="load" data-band=${band}>
          <span
            class="state"
            data-band=${band}
            role="img"
            aria-label=${`${who}: ${percent(totals?.utilization)} allocated`}
          >
            ${
              state && band !== 'none'
                ? html`<span class="i" aria-hidden="true">${state.icon}</span>`
                : nothing
            }
            <span class="v">${band === 'none' ? '—' : percent(totals?.utilization)}</span>
          </span>
        </td>
      </tr>
    </tfoot>`;
  }

  _grid(model) {
    return html`<div class="scroll">
      <table class="matrix">
        <thead>
          <tr>
            <th scope="col" class="corner">Person</th>
            ${model.columns.map((c) => this._columnHead(c))}
            <th scope="col" class="total">Hours / capacity</th>
            <th scope="col" class="load">Load</th>
          </tr>
        </thead>
        <tbody>
          ${repeat(
            model.rows,
            (r) => r.key,
            (r) => this._row(model, r),
          )}
        </tbody>
        ${this._foot(model)}
      </table>
    </div>`;
  }

  // -------------------------------------------------------------------------------------------
  // Chrome

  _controls() {
    const weeks = this._weeks;
    const week = this._resolvedWeek;
    const i = weeks.findIndex((w) => w.week_start === week);
    return html`<div class="controls">
      <span class="group week" role="group" aria-label="Week">
        <button
          type="button"
          data-step="-1"
          ?disabled=${i <= 0}
          title="The week before"
          @click=${() => this._stepWeek(-1)}
        >
          ‹
        </button>
        <select
          aria-label="Week"
          .value=${week ?? ''}
          @change=${(e) => this._setWeek(e.target.value)}
        >
          ${weeks.map(
            (w) =>
              html`<option value=${w.week_start} ?selected=${w.week_start === week}>
                Week of ${weekLabel(w.week_start)}
              </option>`,
          )}
        </select>
        <button
          type="button"
          data-step="1"
          ?disabled=${i < 0 || i >= weeks.length - 1}
          title="The week after"
          @click=${() => this._stepWeek(1)}
        >
          ›
        </button>
      </span>
      <span class="group" role="group" aria-label="Columns to show">
        ${KIND_VOCABULARY.map(
          (k) =>
            html`<label class="kind" title=${k.hint}>
              <input
                type="checkbox"
                data-kind=${k.key}
                .checked=${this._kinds.includes(k.key)}
                @change=${() => this._toggleKind(k.key)}
              />${k.plural}
            </label>`,
        )}
      </span>
      <span class="group">
        Columns
        <span class="segmented" role="group" aria-label="Column order">
          ${COLUMN_SORTS_UI.map(
            (s) =>
              html`<button
                type="button"
                data-column-sort=${s.key}
                aria-pressed=${this._columnSort === s.key ? 'true' : 'false'}
                @click=${() => (this._columnSort = s.key)}
              >
                ${s.label}
              </button>`,
          )}
        </span>
      </span>
      <span class="group">
        People
        <span class="segmented" role="group" aria-label="Row order">
          ${ROW_SORTS_UI.map(
            (s) =>
              html`<button
                type="button"
                data-row-sort=${s.key}
                aria-pressed=${this._rowSort === s.key ? 'true' : 'false'}
                @click=${() => (this._rowSort = s.key)}
              >
                ${s.label}
              </button>`,
          )}
        </span>
      </span>
      <span class="group search">
        <input
          type="search"
          placeholder="Find a project, client or number"
          aria-label="Find a project, client or number"
          .value=${this._q}
          @input=${(e) => (this._q = e.target.value)}
        />
      </span>
    </div>`;
  }

  _kpis(summary) {
    return html`<div class="kpis">
      <div class="kpi" data-kpi="over">
        <div class="n">${count(summary.rows_over)}</div>
        <div class="l">of ${plural(summary.rows, 'person', 'people')} at caution or over</div>
      </div>
      <div class="kpi" data-kpi="columns">
        <div class="n">${count(summary.columns)}</div>
        <div class="l">
          columns · ${plural(summary.per_kind.project, 'project')},
          ${plural(summary.per_kind.recurring, 'retainer')},
          ${plural(summary.per_kind.opportunity, 'deal')}
        </div>
      </div>
      <div class="kpi" data-kpi="unassigned">
        <div class="n">${formatHours(summary.unassigned_hours)}</div>
        <div class="l">role-only, nobody named</div>
      </div>
    </div>`;
  }

  _legend(model) {
    const { palette } = this.appState;
    const slots = new Map();
    for (const c of model.columns)
      if (c.kind !== NONPROJECT_KEY && c.slot) slots.set(c.slot.var, c.slot);
    const typeLabel = (slot) =>
      slot.slot === 8
        ? 'Other'
        : ((palette ?? []).find((t) => t.value === slot.value)?.label ?? humanize(slot.value));
    const caution = STATUS_STATES.find((s) => s.key === 'caution');
    const over = STATUS_STATES.find((s) => s.key === 'overloaded');
    return html`<ul class="legend" aria-label="Legend">
      ${CONFIDENCE_TIERS.map(
        (t) =>
          html`<li>
            <span
              class="sw cell"
              data-fill=${t.fill}
              style="--hue: var(--ink-muted)"
              aria-hidden="true"
            ></span>
            ${t.label} confidence${t.marker ? ` ${t.marker}` : ''}
          </li>`,
      )}
      <li>
        <span
          class="sw cell"
          data-fill="dots"
          data-status="opportunity"
          style="--hue: var(--ink-muted)"
          aria-hidden="true"
        ></span>
        Deal (dashed rule)
      </li>
      <li>
        <span class="sw state" data-band="caution" aria-hidden="true">${caution.icon}</span>
        ${caution.label} total
      </li>
      <li>
        <span class="sw state" data-band="overloaded" aria-hidden="true">${over.icon}</span>
        ${over.label} total
      </li>
      ${[...slots.values()].map(
        (slot) =>
          html`<li data-type=${slot.value ?? 'other'}>
            <span class="type" style=${`--hue: var(${slot.var})`} aria-hidden="true"></span>
            ${typeLabel(slot)}
          </li>`,
      )}
    </ul>`;
  }

  _subtitle(model) {
    const d = this._data;
    const who = d.entity ? `${d.entity.label} (${d.dimension})` : 'Everyone';
    const parts = [
      who,
      `week of ${longDate(d.week_start)}`,
      `${plural(model.rows.length, 'person', 'people')}`,
      `pipeline ${d.pipeline_mode}`,
    ];
    if (model.columns_hidden > 0) parts.push(`${count(model.columns_hidden)} columns hidden`);
    return parts.join(' · ');
  }

  _notes(model, summary) {
    const { scope } = this.appState;
    const notes = [];
    if (model.withheld_count > 0)
      notes.push(
        html`<p class="note" data-note="withheld">
          ${plural(model.withheld_count, 'person is', 'people are')} not shown: your access covers
          ${scope?.unrestricted ? 'everyone' : 'yourself and your reports'} at the individual grain.
          Their ${formatHours(model.totals?.withheld_hours ?? 0)} still count in the column totals'
          hover and in the foot.
        </p>`,
      );
    if (summary.hours_hidden > 0 || (model.unassigned?.hidden_hours ?? 0) > 0)
      notes.push(
        html`<p class="note" data-note="hidden">
          The controls above hide ${count(model.columns_hidden)}
          ${model.columns_hidden === 1 ? 'column' : 'columns'} carrying
          ${formatHours(summary.hours_hidden + (model.unassigned?.hidden_hours ?? 0))}. A row's
          total and its load never follow the controls — a person's week is their week — so the
          hidden hours are stated beneath each total.
        </p>`,
      );
    if (summary.unassigned_hours > 0)
      notes.push(
        html`<p class="note" data-note="unassigned">
          ${formatHours(summary.unassigned_hours)} this week
          ${summary.unassigned_hours === 1 ? 'is' : 'are'} demand with nobody named — role-only
          assignments, inferred role mixes and every deal — in the Role-only row at the foot,
          counting against no one's capacity.
        </p>`,
      );
    if (summary.per_band.none > 0)
      notes.push(
        html`<p class="note" data-note="no-capacity">
          ${plural(summary.per_band.none, 'person has', 'people have')} hours and no capacity this
          week — out all week, or not in the capacity model — so
          ${summary.per_band.none === 1 ? 'their load has' : 'their loads have'} no percentage.
        </p>`,
      );
    if (model.pipeline_mode === 'none')
      notes.push(
        html`<p class="note" data-note="pipeline">
          Opportunities are excluded by the filter bar, so no deal columns are drawn and the totals
          are committed work alone.
        </p>`,
      );
    return notes;
  }

  get _cellColumns() {
    // The specification is shared with the server's CSV (lib/matrix.js); only the drawing is here.
    return withFormats(matrixCellColumns(), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        percent: (v) => percent(v),
      },
      byKey: {
        canonical_type: (v) => humanize(v ?? ''),
        slipped: (v) => (v ? 'yes' : ''),
      },
    });
  }

  get _peopleColumns() {
    return withFormats(matrixPeopleColumns(), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        percent: (v) => percent(v),
      },
      byKey: {
        band: (v) => (v ? html`<status-badge state=${v} compact></status-badge>` : '—'),
      },
    });
  }

  get _columnColumns() {
    return withFormats(matrixColumnColumns(), {
      byUnit: {
        hours: (v) => (v === null || v === undefined ? '—' : formatHours(v, { exact: true })),
        percent: (v) => percent(v),
      },
      byKey: {
        canonical_type: (v) => humanize(v ?? ''),
        slipped: (v) => (v ? 'yes' : ''),
        burn_risk: (v) => (v ? 'yes' : ''),
      },
    });
  }

  render() {
    const { run } = this.appState;
    const model = this._model;
    const summary = model ? matrixSummary(model) : null;

    let body;
    if (!run) body = html`<div class="state-msg">No forecast run yet — nothing to draw.</div>`;
    else if (this._error) body = html`<div class="state-msg error">${this._error}</div>`;
    else if (!model && this._loading)
      body = html`<div class="state-msg">Loading the assignments…</div>`;
    else if (!model) body = html`<div class="state-msg">No week in this window.</div>`;
    else if (!model.rows.length && !model.columns.length)
      body = html`
        ${this._controls()}
        <div class="state-msg">Nobody has hours or capacity in this week for this selection.</div>
      `;
    else
      body = html`
        ${this._controls()} ${this._grid(model)} ${this._legend(model)}
        ${this._notes(model, summary)}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view</summary>
          <data-table
            caption=${`Assignments — ${this._subtitle(model)}`}
            export-name="assignment-matrix"
            page-size="25"
            dense
            .columns=${this._cellColumns}
            .rows=${matrixExportRows(model)}
          ></data-table>
          <data-table
            caption=${`People — ${this._subtitle(model)}`}
            export-name="assignment-matrix-people"
            page-size="25"
            dense
            .columns=${this._peopleColumns}
            .rows=${rowExportRows(model)}
          ></data-table>
          <data-table
            caption=${`Columns — ${this._subtitle(model)}`}
            export-name="assignment-matrix-columns"
            page-size="25"
            dense
            .columns=${this._columnColumns}
            .rows=${columnExportRows(model)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Assignments</div>
            <h2>Who is on what this week?</h2>
            <div class="sub">${model ? this._subtitle(model) : ''}</div>
          </div>
          ${summary ? this._kpis(summary) : nothing}
        </div>
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('assignment-matrix', AssignmentMatrix);
